import FormBuilderOb from '@components/FormBuilder/FormBuilder.ob';
import MarketingConsentForm from '@components/MarketingConsentForm/MarketingConsentForm.component';
import { Box, LuxCheckbox, LuxTypography, useTheme } from '@gemini/fe-ds-core';
import { NestedObject } from '@gemini/lux-ds-pms';
import { FormIds } from '@utils/enums/FormIds.enum';
import {
  CheckBoxWrapper,
  ContactDetailsWrapper,
} from './ContactDetailsPage.styles';
import { ContactDetailsInfo, usePatientStore } from '@store/patientStore';
import { useOnlineBookingStore } from '@store/onlineBookingStore';
import { ChangeEvent, useEffect, useMemo, useRef, useState } from 'react';
import useAnalytics from '@hooks/useAnalytics';
import { FlowsPages } from '@utils/enums/FlowsPages.enum';
import { useTranslation } from 'react-i18next';

interface ContactDetailsProps {
  setEnableButton: (val: boolean) => void;
}

const hasFieldValue = (value?: unknown) =>
  value !== undefined && value !== null && String(value).trim() !== '';

const ContactDetailsPage = ({ setEnableButton }: ContactDetailsProps) => {
  const { trackPageAnalytics } = useAnalytics();
  const { t } = useTranslation();
  const theme = useTheme();
  const updateContactDetails = usePatientStore(
    state => state.updateContactDetails,
  );

  const brandName = useOnlineBookingStore(state => state.brandName);
  const setCheckboxValue = useOnlineBookingStore(
    state => state.setContactDetailsCheckboxStatus,
  );
  const checkboxValue = useOnlineBookingStore(
    state => state.contactDetailsCheckboxStatus,
  );

  const links = useOnlineBookingStore(state => state.links);

  const patients = usePatientStore(state => state.patients);
  const contactsDetails = usePatientStore(state => state.contactsDetails);
  const setShouldSave = useOnlineBookingStore(state => state.setShouldSave);
  const contactDetailsImportInitialized = useRef(false);
  const [contactDetailsValid, setContactDetailsValid] = useState(false);
  const [marketingConsentValid, setMarketingConsentValid] = useState(true);

  const patientDetailsToImport = useMemo(() => {
    const availablePatients = patients.filter(patient => patient);
    if (!availablePatients.length) return undefined;

    const firstPatientOrder = Math.min(
      ...availablePatients.map(patient =>
        typeof patient?.order !== 'undefined' ? patient.order : 99,
      ),
    );

    return (
      availablePatients.find(patient => patient?.order === firstPatientOrder) ||
      availablePatients[0]
    )?.patientDetails;
  }, [patients]);

  const hasPatientDetailsToImport = useMemo(
    () =>
      [
        patientDetailsToImport?.firstName,
        patientDetailsToImport?.lastName,
        patientDetailsToImport?.dateOfBirth,
        patientDetailsToImport?.email,
        patientDetailsToImport?.phone?.number,
      ].some(hasFieldValue),
    [patientDetailsToImport],
  );

  const hasContactDetailsValues = useMemo(
    () =>
      [
        contactsDetails?.firstName,
        contactsDetails?.lastName,
        contactsDetails?.dateOfBirth,
        contactsDetails?.email,
        contactsDetails?.phone?.number,
      ].some(hasFieldValue),
    [contactsDetails],
  );

  useEffect(() => {
    if (contactDetailsImportInitialized.current || !hasPatientDetailsToImport) {
      return;
    }

    contactDetailsImportInitialized.current = true;

    if (
      checkboxValue === undefined ||
      (!hasContactDetailsValues && checkboxValue !== true)
    ) {
      setCheckboxValue(true);
    }
  }, [
    checkboxValue,
    hasContactDetailsValues,
    hasPatientDetailsToImport,
    setCheckboxValue,
  ]);

  const shouldResetContactDetails = checkboxValue ?? hasPatientDetailsToImport;

  //ANALYTICS (RONA only) - Review
  useEffect(() => {
    if (!brandName) return;
    const timeout = window.setTimeout(() => {
      trackPageAnalytics({
        page: FlowsPages.CONTACT_DETAILS,
        patients,
      });
    }, 100); // slight delay to ensure we wanted to land here and have not restarted from step 1 (isStartedNewPatient === false)
    return () => window.clearTimeout(timeout);
  }, [brandName, patients, trackPageAnalytics]);

  const handleContactDetailsForm = (e: ChangeEvent<HTMLInputElement>) => {
    setCheckboxValue(e.target.checked);
  };

  useEffect(() => {
    setEnableButton(contactDetailsValid && marketingConsentValid);
  }, [contactDetailsValid, marketingConsentValid, setEnableButton]);

  const formIds = useMemo(() => [FormIds.ContactInformation], []);

  return (
    <ContactDetailsWrapper>
      <CheckBoxWrapper>
        <LuxCheckbox
          label={
            <LuxTypography variant="textXs" fontSize={'0.875rem'}>
              {t('fieldsLabel.sameAsPatientDetails')}
            </LuxTypography>
          }
          onChange={e => handleContactDetailsForm(e)}
          // defaultChecked={checkboxValue}
          checked={shouldResetContactDetails}
        />
      </CheckBoxWrapper>
      <FormBuilderOb
        key={FormIds.ContactInformation}
        formIds={formIds}
        setEnableButton={setContactDetailsValid}
        onUnmount={(formData?: NestedObject) => {
          const contactsDetailsData = formData?.contactsDetails as
            | ContactDetailsInfo
            | undefined;

          if (!contactsDetailsData) {
            return;
          }

          const currentContactsDetails =
            usePatientStore.getState().contactsDetails;
          updateContactDetails({
            ...contactsDetailsData,
            customFieldsMarketing: currentContactsDetails.customFieldsMarketing,
            storeCustomFieldsMarketing:
              currentContactsDetails.storeCustomFieldsMarketing,
          });
          setShouldSave(true);
        }}
        resetFieldsContactsDetails={shouldResetContactDetails}
        mykey="CONTACT_DETAILS"
      />
      <MarketingConsentForm onValidate={setMarketingConsentValid} />
      {brandName && (
        <Box sx={{ padding: '0 1rem 1rem 1rem' }}>
          <LuxTypography fontFamily={theme.palette.LUX.obTokens.textSmFamily}>
            <span
              dangerouslySetInnerHTML={{
                __html:
                  t('contactDetailsPage.declaration', {
                    termsAndConditions: `<a href="${links?.termsAndConditions}" target="_blank" rel="noopener noreferrer" style="color:inherit;"><strong>${t('contactDetailsPage.termsAndConditions')}</strong></a>`,
                    privacyPolicy: `<a href="${links?.privacyPolicy}" target="_blank" rel="noopener noreferrer" style="color:inherit;"><strong>${t('contactDetailsPage.privacyPolicy')}</strong></a>`,
                  }) ?? '',
              }}
            ></span>
          </LuxTypography>
        </Box>
      )}
    </ContactDetailsWrapper>
  );
};

export default ContactDetailsPage;
